"use client";

import {
    useEffect,
    useRef,
} from "react";
import {
    usePathname,
} from "next/navigation";

const META_PIXEL_ID =
    "1090555203627226";

const COOKIE_CONSENT_KEY =
    "cookieConsent";

const META_PIXEL_SCRIPT_ID =
    "meta-pixel-script";

const IS_PRODUCTION =
    process.env.NODE_ENV ===
    "production";

function isMetaPixelEnabled(
    consent
) {
    return (
        IS_PRODUCTION ||
        consent
            ?.developmentTracking ===
        true
    );
}

function shouldLog() {
    return (
        !IS_PRODUCTION &&
        window
            ?.metaPixelDevelopmentTracking ===
        true
    );
}

function metaLog(
    message,
    details
) {
    if (
        typeof window ===
        "undefined" ||
        !shouldLog()
    ) {
        return;
    }

    if (
        details !== undefined
    ) {
        console.log(
            message,
            details
        );

        return;
    }

    console.log(
        message
    );
}

function metaError(
    message,
    error
) {
    if (
        typeof window ===
        "undefined" ||
        !shouldLog()
    ) {
        return;
    }

    console.error(
        message,
        error
    );
}

function getSavedConsent() {
    try {
        const savedConsent =
            window.sessionStorage.getItem(
                COOKIE_CONSENT_KEY
            );

        if (!savedConsent) {
            return null;
        }

        return JSON.parse(
            savedConsent
        );
    } catch (error) {
        metaError(
            "[META] A mentett hozzájárulás nem olvasható.",
            error
        );

        return null;
    }
}

function deleteCookie(
    name
) {
    const hostname =
        window.location.hostname;

    const domains = [
        "",
        hostname,
        `.${hostname}`,
        ".strike.hu",
    ];

    domains.forEach(
        (domain) => {
            const domainPart =
                domain
                    ? `; domain=${domain}`
                    : "";

            document.cookie =
                `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
        }
    );
}

function removeMetaCookies() {
    deleteCookie(
        "_fbp"
    );

    deleteCookie(
        "_fbc"
    );
}

function sendMetaPageView() {
    if (
        typeof window ===
            "undefined" ||
        typeof window.fbq !==
            "function" ||
        !window
            .metaPixelInitialized ||
        !window
            .metaPixelTrackingEnabled
    ) {
        return;
    }

    window.fbq(
        "track",
        "PageView"
    );

    metaLog(
        "[META] PageView elküldve.",
        {
            pageLocation:
                window.location
                    .href,
            pageTitle:
                document.title,
        }
    );
}

function initializeMetaPixel(
    consent
) {
    if (
        typeof window ===
        "undefined" ||
        !isMetaPixelEnabled(
            consent
        )
    ) {
        return;
    }

    window
        .metaPixelDevelopmentTracking =
        consent
            ?.developmentTracking ===
        true;

    if (
        !META_PIXEL_ID ||
        META_PIXEL_ID ===
        "IDE_JON_A_META_PIXEL_ID"
    ) {
        return;
    }

    if (
        window
            .metaPixelInitialized
    ) {
        window
            .metaPixelTrackingEnabled =
            true;

        if (
            typeof window.fbq ===
            "function"
        ) {
            window.fbq(
                "consent",
                "grant"
            );
        }

        return;
    }

    window
        .metaPixelInitialized =
        true;

    window
        .metaPixelTrackingEnabled =
        true;

    if (
        !window.fbq
    ) {
        const fbq =
            function fbq() {
                fbq.callMethod
                    ? fbq.callMethod.apply(
                        fbq,
                        arguments
                    )
                    : fbq.queue.push(
                        arguments
                    );
            };

        window.fbq =
            fbq;

        if (
            !window._fbq
        ) {
            window._fbq =
                fbq;
        }

        fbq.push =
            fbq;

        fbq.loaded =
            true;

        fbq.version =
            "2.0";

        fbq.queue =
            [];
    }

    window.fbq(
        "consent",
        "grant"
    );

    window.fbq(
        "set",
        "autoConfig",
        false,
        META_PIXEL_ID
    );

    window.fbq(
        "init",
        META_PIXEL_ID
    );

    const existingScript =
        document.getElementById(
            META_PIXEL_SCRIPT_ID
        );

    if (
        !existingScript
    ) {
        const script =
            document.createElement(
                "script"
            );

        script.id =
            META_PIXEL_SCRIPT_ID;

        script.async =
            true;

        script.src =
            "https://connect.facebook.net/en_US/fbevents.js";

        script.addEventListener(
            "load",
            () => {
                metaLog(
                    "[META] Meta Pixel betöltve."
                );
            }
        );

        script.addEventListener(
            "error",
            (error) => {
                metaError(
                    "[META] Meta Pixel betöltése sikertelen.",
                    error
                );
            }
        );

        document.head.appendChild(
            script
        );
    }

    sendMetaPageView();
}

function disableMetaPixelTracking() {
    if (
        typeof window ===
        "undefined"
    ) {
        return;
    }

    window
        .metaPixelTrackingEnabled =
        false;

    window
        .metaPixelDevelopmentTracking =
        false;

    if (
        typeof window.fbq ===
        "function"
    ) {
        window.fbq(
            "consent",
            "revoke"
        );
    }

    removeMetaCookies();

    metaLog(
        "[META] Marketing hozzájárulás visszavonva."
    );
}

export function trackMetaEvent(
    eventName,
    parameters = {}
) {
    if (
        typeof window ===
        "undefined" ||
        typeof window.fbq !==
        "function" ||
        !window
            .metaPixelInitialized ||
        !window
            .metaPixelTrackingEnabled
    ) {
        return;
    }

    window.fbq(
        "track",
        eventName,
        parameters
    );
}

export default function MetaPixel() {
    const pathname =
        usePathname();

    const previousPathnameRef =
        useRef(null);

    const skipNextPageViewRef =
        useRef(false);

    useEffect(() => {
        const handleConsentSaved =
            (event) => {
                const nextConsent =
                    event.detail
                        ?.consent ||
                    null;

                if (
                    nextConsent
                        ?.marketing ===
                        true &&
                    isMetaPixelEnabled(
                        nextConsent
                    )
                ) {
                    const wasInitialized =
                        window
                            .metaPixelInitialized ===
                        true;

                    initializeMetaPixel(
                        nextConsent
                    );

                    if (
                        !wasInitialized
                    ) {
                        skipNextPageViewRef.current =
                            true;
                    }

                    return;
                }

                disableMetaPixelTracking();
            };

        const savedConsent =
            getSavedConsent();

        if (
            savedConsent
                ?.marketing ===
                true &&
            isMetaPixelEnabled(
                savedConsent
            )
        ) {
            initializeMetaPixel(
                savedConsent
            );

            skipNextPageViewRef.current =
                true;
        } else {
            disableMetaPixelTracking();
        }

        window.addEventListener(
            "cookie-consent-saved",
            handleConsentSaved
        );

        return () => {
            window.removeEventListener(
                "cookie-consent-saved",
                handleConsentSaved
            );
        };
    }, []);

    useEffect(() => {
        if (
            !pathname
        ) {
            return;
        }

        if (
            previousPathnameRef.current ===
            null
        ) {
            previousPathnameRef.current =
                pathname;

            return;
        }

        if (
            previousPathnameRef.current ===
            pathname
        ) {
            return;
        }

        previousPathnameRef.current =
            pathname;

        if (
            skipNextPageViewRef.current
        ) {
            skipNextPageViewRef.current =
                false;

            return;
        }

        sendMetaPageView();
    }, [
        pathname,
    ]);

    return null;
}